import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import LegalNav from '../components/LegalNav';
import './LegalPage.css';

function About() {
  useEffect(() => {
    document.title = 'About My Fair Share - Free UK Tax Calculator';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Learn about My Fair Share, the free UK tax calculator. Understand how we calculate your take-home pay using official HMRC rates and help you see where your money goes.');
    }
  }, []);

  return (
    <div className="legal-page">
      <div className="legal-container">
        <Link to="/" className="back-link">← Back to Calculator</Link>
        
        <h1>About My Fair Share</h1>
        <p className="last-updated">Your free UK tax calculator</p>

        <section>
          <h2>1. Our Mission</h2>
          <p>
            My Fair Share was created with a simple goal: to help ordinary people understand their taxes 
            and see how their money could go further. We believe everyone deserves to know exactly 
            how much of their hard-earned salary goes to taxes and what could change if the 
            government raised revenue from alternative sources.
          </p>
          <p>
            We're not here to tell you what's right or wrong politically. We're here to give you 
            the facts and figures so you can make up your own mind. Our calculator shows you the 
            real numbers based on current HMRC guidelines.
          </p>
        </section>

        <section>
          <h2>2. What Does My Fair Share Do?</h2>
          <p>
            My Fair Share is a free, easy-to-use UK tax calculator that shows you:
          </p>
          <ul>
            <li><strong>Your take-home pay:</strong> How much money actually lands in your bank account after tax</li>
            <li><strong>Your tax breakdown:</strong> Exactly how much goes to Income Tax and National Insurance</li>
            <li><strong>What-if scenarios:</strong> How much more you could keep if the government raised revenue differently</li>
          </ul>
          <p>
            Everything is calculated instantly in your browser. We never store your salary information 
            anywhere – it stays completely private.
          </p>
        </section>

        <section>
          <h2>3. How We Calculate Your Tax</h2>
          <p>
            Our calculations follow the official HMRC guidelines for the 2025-2026 tax year. Here's 
            how UK tax actually works:
          </p>
          
          <h3>3.1 Income Tax Bands</h3>
          <p>
            Your salary is divided into different bands, each taxed at a different rate:
          </p>
          <ul>
            <li><strong>Personal Allowance (£0 - £12,570):</strong> You pay 0% tax on this amount – it's completely tax-free</li>
            <li><strong>Basic Rate (£12,571 - £50,270):</strong> You pay 20% tax on earnings in this band</li>
            <li><strong>Higher Rate (£50,271 - £125,140):</strong> You pay 40% tax on earnings in this band</li>
            <li><strong>Additional Rate (Over £125,140):</strong> You pay 45% tax on earnings above this threshold</li>
          </ul>
          <p>
            <strong>Important note:</strong> If you earn over £100,000, your Personal Allowance gradually 
            reduces. For every £2 you earn above £100,000, you lose £1 of your Personal Allowance. 
            This means someone earning £125,140 or more has no Personal Allowance at all.
          </p>

          <h3>3.2 National Insurance Contributions</h3>
          <p>
            On top of Income Tax, you also pay National Insurance (NI) to fund state benefits and 
            the NHS:
          </p>
          <ul>
            <li><strong>Below £12,570:</strong> You pay no National Insurance</li>
            <li><strong>£12,570 - £50,270:</strong> You pay 8% NI on earnings in this range</li>
            <li><strong>Above £50,270:</strong> You pay 2% NI on earnings above this threshold</li>
          </ul>
          <p>
            Your employer also pays NI on top of your salary, but we focus on what comes out of your pay packet.
          </p>

          <h3>3.3 Calculating Your Take-Home Pay</h3>
          <p>
            We take your gross salary, subtract your Income Tax, subtract your National Insurance, 
            and show you exactly what's left. It's that simple. No hidden fees, no complications.
          </p>
        </section>

        <section>
          <h2>4. Understanding What-If Scenarios</h2>
          <p>
            Our "What If" scenarios show you how much extra money you could keep if the government 
            raised the same amount of revenue from alternative sources instead of taxing your income.
          </p>
          <p>
            Each scenario represents real policy proposals that have been studied by economists, 
            think tanks, and government bodies. We use their revenue estimates to calculate how 
            much your personal tax bill could potentially decrease.
          </p>
          
          <h3>4.1 How the Calculation Works</h3>
          <p>
            The UK raises approximately £475.3 billion in total tax revenue each year. When 
            alternative revenue sources bring in extra money, we calculate what percentage of the 
            total tax bill that represents. Your personal tax saving is then calculated as that 
            same percentage of your own tax bill.
          </p>
          <p>
            For example, if a new policy raises £20 billion annually, that's about 4.2% of the 
            total tax pot. So someone paying £10,000 in tax could potentially save around £420 
            per year.
          </p>

          <h3>4.2 Real Numbers, Real Sources</h3>
          <p>
            Every figure we use comes from reputable sources like:
          </p>
          <ul>
            <li>HMRC official statistics and tax gap reports</li>
            <li>The Institute for Fiscal Studies (IFS)</li>
            <li>The Resolution Foundation</li>
            <li>The London School of Economics (LSE)</li>
            <li>Tax Justice UK</li>
          </ul>
          <p>
            We link to every source so you can verify the numbers yourself and learn more about 
            each policy proposal.
          </p>
        </section>

        <section>
          <h2>5. Sample Scenarios: See the Real Impact</h2>
          <p>
            Here are some real examples to show you how our calculator works in practice:
          </p>

          <h3>5.1 Scenario A: £35,000 Salary (Basic Rate Taxpayer)</h3>
          <p>
            <strong>Sarah</strong> is a teaching assistant earning £35,000 per year.
          </p>
          <p>
            <strong>Current situation:</strong>
          </p>
          <ul>
            <li>Personal Allowance: £12,570 (tax-free)</li>
            <li>Taxable income: £22,430</li>
            <li>Income Tax (20% of £22,430): £4,486</li>
            <li>National Insurance (8% of £22,430): £1,794</li>
            <li>Total deductions: £6,280</li>
            <li>Take-home pay: £28,720 per year (£2,393 per month)</li>
          </ul>
          <p>
            <strong>What if scenarios:</strong>
          </p>
          <ul>
            <li>If billionaires paid a wealth tax (£20bn raised): Sarah could save approximately <strong>£264 per year</strong></li>
            <li>If corporate tax avoidance was eliminated (£14.5bn raised): Sarah could save approximately <strong>£192 per year</strong></li>
            <li>Combined savings with multiple policies: Sarah could potentially take home an extra <strong>£500-600 per year</strong></li>
          </ul>

          <h3>5.2 Scenario B: £65,000 Salary (Higher Rate Taxpayer)</h3>
          <p>
            <strong>James</strong> is a project manager earning £65,000 per year.
          </p>
          <p>
            <strong>Current situation:</strong>
          </p>
          <ul>
            <li>Personal Allowance: £12,570 (tax-free)</li>
            <li>Basic rate band (£12,571 - £50,270): £7,540 tax at 20%</li>
            <li>Higher rate band (£50,271 - £65,000): £5,892 tax at 40%</li>
            <li>Total Income Tax: £13,432</li>
            <li>National Insurance: £3,310</li>
            <li>Total deductions: £16,742</li>
            <li>Take-home pay: £48,258 per year (£4,021 per month)</li>
          </ul>
          <p>
            <strong>What if scenarios:</strong>
          </p>
          <ul>
            <li>If billionaires paid a wealth tax (£20bn raised): James could save approximately <strong>£704 per year</strong></li>
            <li>If capital gains were taxed like income (£16bn raised): James could save approximately <strong>£564 per year</strong></li>
            <li>Combined savings with all policies active: James could potentially take home an extra <strong>£1,500-2,000 per year</strong></li>
          </ul>

          <h3>5.3 Scenario C: £100,000 Salary (Approaching Personal Allowance Taper)</h3>
          <p>
            <strong>Emma</strong> is a senior consultant earning £100,000 per year.
          </p>
          <p>
            <strong>Current situation:</strong>
          </p>
          <ul>
            <li>Personal Allowance: £12,570 (still fully available at exactly £100k)</li>
            <li>Basic rate band: £7,540 tax at 20%</li>
            <li>Higher rate band (£50,271 - £100,000): £19,892 tax at 40%</li>
            <li>Total Income Tax: £27,432</li>
            <li>National Insurance: £4,010</li>
            <li>Total deductions: £31,442</li>
            <li>Take-home pay: £68,558 per year (£5,713 per month)</li>
          </ul>
          <p>
            <strong>What if scenarios:</strong>
          </p>
          <ul>
            <li>If billionaires paid a wealth tax (£20bn raised): Emma could save approximately <strong>£1,322 per year</strong></li>
            <li>If Brexit costs were recovered (£40bn raised): Emma could save approximately <strong>£2,644 per year</strong></li>
            <li>Combined savings with all policies active: Emma could potentially take home an extra <strong>£3,000-4,000 per year</strong></li>
          </ul>

          <h3>5.4 Scenario D: £150,000 Salary (Additional Rate Taxpayer)</h3>
          <p>
            <strong>Michael</strong> is a company director earning £150,000 per year.
          </p>
          <p>
            <strong>Current situation:</strong>
          </p>
          <ul>
            <li>Personal Allowance: £0 (completely tapered away at this income level)</li>
            <li>Basic rate band: £10,054 tax at 20%</li>
            <li>Higher rate band: £29,948 tax at 40%</li>
            <li>Additional rate band (above £125,140): £11,187 tax at 45%</li>
            <li>Total Income Tax: £51,189</li>
            <li>National Insurance: £5,010</li>
            <li>Total deductions: £56,199</li>
            <li>Take-home pay: £93,801 per year (£7,817 per month)</li>
          </ul>
          <p>
            <strong>What if scenarios:</strong>
          </p>
          <ul>
            <li>If all proposed alternative revenue sources were implemented: Michael could potentially save <strong>£5,000-6,000 per year</strong></li>
          </ul>
          <p>
            <strong>Note:</strong> These figures are approximations based on the proportional reduction 
            calculation and may vary slightly from the actual calculator results due to rounding.
          </p>
        </section>

        <section>
          <h2>6. Important Things to Know</h2>
          
          <h3>6.1 This is Not Financial Advice</h3>
          <p>
            My Fair Share is designed to give you a general understanding of how UK taxes work and 
            estimate your tax bill. However, your actual tax situation might be different if you:
          </p>
          <ul>
            <li>Have multiple sources of income</li>
            <li>Receive benefits or tax credits</li>
            <li>Have pension contributions deducted from your salary</li>
            <li>Pay into a salary sacrifice scheme</li>
            <li>Have student loan repayments</li>
            <li>Are self-employed or have freelance income</li>
          </ul>
          <p>
            For specific advice about your taxes, please consult HMRC directly or speak to a 
            qualified tax professional.
          </p>

          <h3>6.2 The What-If Scenarios Are Hypothetical</h3>
          <p>
            Our alternative revenue scenarios are based on serious policy proposals and real 
            economic research. However, they are hypothetical – they show what <em>could</em> happen, 
            not what <em>will</em> happen.
          </p>
          <p>
            Tax policy is complex, and any changes would need to go through Parliament and could 
            have other economic effects not captured in these simple calculations. We present these 
            scenarios to inform and spark discussion, not to predict the future.
          </p>

          <h3>6.3 We Keep Your Data Private</h3>
          <p>
            Your salary and all calculations are processed entirely within your browser. We never:
          </p>
          <ul>
            <li>Store your salary on our servers</li>
            <li>Send your data to third parties</li>
            <li>Track your individual calculations</li>
            <li>Use your information for marketing</li>
          </ul>
          <p>
            When you close or refresh the page, your data is gone. Read our full{' '}
            <Link to="/privacy">Privacy Policy</Link> for more details.
          </p>
        </section>

        <section>
          <h2>7. Frequently Asked Questions</h2>
          
          <h3>Q: Is My Fair Share free to use?</h3>
          <p>
            Yes! My Fair Share is completely free. No subscriptions, no hidden fees, no premium features 
            locked behind a paywall.
          </p>

          <h3>Q: How accurate is the calculator?</h3>
          <p>
            Our calculator uses the official HMRC tax rates and thresholds for 2025-2026. For most 
            employed people with a single job and no complications, it should be very accurate. 
            However, it doesn't account for things like pension contributions, student loans, or 
            benefits – these would need to be calculated separately.
          </p>

          <h3>Q: Where do the "What If" savings come from?</h3>
          <p>
            Each scenario is based on published research and government data about potential 
            alternative revenue sources. We link to all our sources so you can read the original 
            reports yourself.
          </p>

          <h3>Q: Can I use this for tax planning?</h3>
          <p>
            My Fair Share gives you a good general picture of your tax situation, but it's not a 
            replacement for professional tax advice. If you're making important financial 
            decisions, please consult a qualified accountant or tax adviser.
          </p>

          <h3>Q: Does this work for Scotland?</h3>
          <p>
            Currently, My Fair Share uses the standard UK (England, Wales, and Northern Ireland) tax 
            bands. Scotland has slightly different income tax rates, so Scottish taxpayers may 
            see different results in reality. We hope to add Scottish tax rates in a future update.
          </p>

          <h3>Q: Why did you make My Fair Share?</h3>
          <p>
            We believe everyone should have easy access to clear, honest information about their 
            taxes. Too often, tax is made to seem complicated and scary. We wanted to create 
            something simple that empowers people to understand where their money goes.
          </p>
        </section>

        <section>
          <h2>8. Get in Touch</h2>
          <p>
            Have questions, suggestions, or spotted an error? We'd love to hear from you!
          </p>
          <p>
            Visit our <Link to="/contact">Contact page</Link> to get in touch with our team.
          </p>
          <p>
            Thank you for using My Fair Share. We hope it helps you understand your taxes better 
            and sparks some interesting conversations about how we fund public services.
          </p>
        </section>

        <LegalNav excludeLinks={['/about']} />
      </div>
    </div>
  );
}

export default About;
